import React from 'react';

const SuggestionsTable = ({
  suggestionsData,
  selectedItems,
  handleCheckboxSelection,
  searchType,
  selectedFilter,
}) => {
  if (!Array.isArray(suggestionsData) || suggestionsData.length === 0) return null;

  const uniqueResults = new Set();
  const uniqueNotFound = new Set();
  const uniqueValidationErrors = new Set();
  let rowNumber = 1;

  const renderColumnHeader = () => {
    switch (searchType) {
      case 'DISEASES':
        return (
          <>
            <th></th>
            <th>{getDiseaseItemColumnHeader()}</th>
            <th>Disease Name</th>
            {selectedFilter !== 'diseaseName' && selectedFilter !== 'diseaseUMLSCUI' && <th>Disease CUI</th>}
            <th>Select</th>
          </>
        );
      case 'VARIANTS':
        return (
          <>
            <th></th>
            <th>{getVariantItemColumnHeader()}</th>
            <th>Variants Gene</th>
            <th>Select</th>
          </>
        );
      case 'GENES':
        return (
          <>
            <th></th>
            <th>{getGeneItemColumnHeader()}</th>
            <th>{selectedFilter === 'geneID' ? 'ENSEMBL ID' : 'Gene ID'}</th>
            <th>{getGeneSecondColumnHeader()}</th>
            <th>Select</th>
          </>
        );
      case 'CHEMICALS':
        return (
          <>
            <th></th>
            <th>{getChemicalColumnHeader()}</th>
            <th>Chemicals Name</th>
            <th>Select</th>
          </>
        );
      default:
        return null;
    }
  };

  const getDiseaseItemColumnHeader = () => {
    switch (selectedFilter) {
      case 'diseaseUMLSCUI':
        return 'Disease CUI';
      case 'MSH':
        return 'MESH';
      case 'diseaseName':
        return 'Disease CUI';
      default:
        return selectedFilter;
    }
  };

  const getVariantItemColumnHeader = () => {
    switch (selectedFilter) {
      case 'strID':
        return 'VARIANT rs number';
      case 'chromocoord':
        return 'Chrom. position';
      default:
        return selectedFilter;
    }
  };

  const getGeneItemColumnHeader = () => {
    switch (selectedFilter) {
      case 'symbol':
        return 'Gene Symbol';
      case 'ensemblID':
        return 'Ensembl ID';
      case 'uniprotID':
        return 'Uniprot ID';
      case 'geneID':
        return 'Gene ID';
      default:
        return selectedFilter;
    }
  };

  const getGeneSecondColumnHeader = () => {
    if (selectedFilter === 'symbol') {
      return 'Ensembl IDs';
    }
    return 'Gene Symbol';
  };

  const getChemicalColumnHeader = () => {
    if (selectedFilter === 'CHEBI') {
      return 'CHEBI IDs';
    } else if (selectedFilter === 'DRUGBANK') {
      return 'DRUGBANK IDs';
    } else if (selectedFilter === 'MESH') {
      return 'MESH IDs';
    } else if (selectedFilter === 'CHEMBL') {
      return 'CHEMBL IDs';
    } else if (selectedFilter === 'PUBCHEM') {
      return 'PUBCHEM IDs';
    }
  };

  const renderValidationErrorRow = (item, index) => (
    <tr key={index} className="validation-error">
      <td style={{ color: 'red', fontStyle: 'italic' }}>{rowNumber++}</td>
      <td style={{ color: 'red', fontStyle: 'italic' }}>{item.query}</td>
      <td style={{ color: 'red', fontStyle: 'italic' }}>{item.validationError}</td>
      {searchType === 'DISEASES' && selectedFilter !== 'diseaseName' && selectedFilter !== 'diseaseUMLSCUI' && <td></td>}
      {searchType === 'GENES' && <td></td>}
      <td></td>
    </tr>
  );

  const renderNotFoundRow = (item, index) => {
    return (
      <tr key={index} className="validation-error">
        {selectedFilter === 'diseaseName' ? (
          <>
            <td style={{ color: 'red', fontStyle: 'italic' }}>{rowNumber++}</td>
            <td style={{ color: 'red', fontStyle: 'italic' }}>
              {item.validationError ? item.validationError : 'Not available'}
            </td>
            <td style={{ color: 'red', fontStyle: 'italic' }}>{item.query}</td>
            <td></td>
          </>
        ) : (
          <>
            <td style={{ color: 'red', fontStyle: 'italic' }}>{rowNumber++}</td>
            <td style={{ color: 'red', fontStyle: 'italic' }}>{item.query}</td>
            <td style={{ color: 'red', fontStyle: 'italic' }}>
              {item.validationError ? item.validationError : 'Not available'}
            </td>
            <td></td>
            <td></td>
          </>
        )}
      </tr>
    );
  };

  const renderResultRow = (result, index, i, query) => (
    <tr key={`${index}-${i}`}>
      <td>{rowNumber++}</td>
      <td>{getResultColumnValue(result, query)}</td>
      <td>{getResultName(result)}</td>
      {searchType === 'DISEASES' && selectedFilter !== 'diseaseName' && selectedFilter !== 'diseaseUMLSCUI'&& renderSecondColumn(result)}
      {searchType === 'GENES' && renderSecondColumn(result)}
      <td>
        <input
          type="checkbox"
          name="selectedItem"
          value={result[selectedFilter]}
          checked={selectedItems.includes(result)}
          onChange={() => handleCheckboxSelection(result)}
          className="ml-3"
        />
      </td>
    </tr>
  );

  const getResultColumnValue = (result, query) => {
    switch (searchType) {
      case 'DISEASES':
        return getDiseaseColumnValue(result, query);
      case 'VARIANTS':
        return getVariantColumnValue(result);
      case 'GENES':
        return getGeneColumnValue(result);
      case 'CHEMICALS':
        return getCleanChemicalColumnValue(result);
      default:
        return 'Not available';
    }
  };

  const getDiseaseColumnValue = (result, query) => {
    switch (selectedFilter) {
      case 'diseaseUMLSCUI':
        return Array.isArray(result.diseaseUMLSCUI)
          ? result.diseaseUMLSCUI.join(', ')
          : result.diseaseUMLSCUI || 'Not available';
      case 'MSH':
        return Array.isArray(result.msh) ? result.msh.join(', ') : result.msh || 'Not available';
      case 'OMIM':
        return Array.isArray(result.omim)
          ? result.omim.find(i => i === query)
          : result.omim || 'Not available';
      case 'NCI':
        return Array.isArray(result.nci) ? result.nci.join(', ') : result.nci || 'Not available';
      case 'ORDO':
        return Array.isArray(result.ordo) ? result.ordo.join(', ') : result.ordo || 'Not available';
      case 'HPO':
        return Array.isArray(result.hpo) ? result.hpo.join(', ') : result.hpo || 'Not available';
      case 'MONDO':
        return Array.isArray(result.mondo)
          ? result.mondo.find(i => i === query)
          : result.mondo || 'Not available';
      case 'EFO':
        return Array.isArray(result.efo) 
          ? result.efo.find(i => i === query)
          : result.efo || 'Not available';
      case 'diseaseName':
        return result && result.diseaseUMLSCUI ? result.diseaseUMLSCUI : 'Not available';
      default:
        return 'Not available';
    }
  };

  const getVariantColumnValue = result => {
    switch (selectedFilter) {
      case 'strID':
        return Array.isArray(result.strID) ? result.strID.join(', ') : result.strID || 'Not available';
      case 'chromocoord':
        return result.chromosome ? `${result.chromosome}:${result.coord}` : 'Not available';
      default:
        return 'Not available';
    }
  };

  const getGeneColumnValue = result => {
    switch (selectedFilter) {
      case 'symbol':
        return Array.isArray(result.symbol) ? result.symbol.join(', ') : result.symbol || 'Not available';
      case 'geneID':
        return Array.isArray(result.geneID) ? result.geneID.join(', ') : result.geneID || 'Not available';
      case 'ensemblID':
        return Array.isArray(result.ensembl_ids)
          ? result.ensembl_ids.join(', ')
          : result.ensembl_ids || 'Not available';
      case 'uniprotID':
        return Array.isArray(result.proteinStrIDs)
          ? result.proteinStrIDs.join(', ')
          : result.proteinStrIDs || 'Not available';
      default:
        return 'Not available';
    }
  };

  const getChemicalColumnValue = result => {
    switch (selectedFilter) {
      case 'CHEBI':
        return Array.isArray(result.chebi[0])
          ? result.chebi[0][0].join(', ')
          : result.chebi[0] || 'Not available';
      case 'DRUGBANK':
        return Array.isArray(result.drugbank[0])
          ? result.drugbank[0][0].join(', ')
          : result.drugbank[0] || 'Not available';
      case 'MESH':
        return Array.isArray(result.mesh[0])
          ? result.mesh[0][0].join(', ')
          : result.mesh[0] || 'Not available';
      case 'CHEMBL':
        return Array.isArray(result.chembl[0])
          ? result.chembl[0][0].join(', ')
          : result.chembl[0] || 'Not available';
      case 'PUBCHEM':
        return Array.isArray(result.pubchem[0])
          ? result.pubchem[0][0].join(', ')
          : result.pubchem[0] || 'Not available';
      default:
        return 'Not available';
    }
  };

  const getCleanChemicalColumnValue = value => {
    // the raw data non-chemId values can be like __{id}__someName
    // we need to clean it up to just {id}
    const label = getChemicalColumnValue(value);
    let clean = typeof label === 'string' ? label.replace(/^_+/, '') : label;
    clean = typeof clean === 'string' ? clean.replace(/_+$/, '') : clean;
    // Remove anything after matching "___"
    clean = typeof clean === 'string' ? clean.split('___')[0] : clean;
    return clean || 'Not available';
  };

  const getResultName = result => {
    switch (searchType) {
      case 'DISEASES':
        return result.name;
      case 'VARIANTS':
        return result.geneSymbol;
      case 'GENES':
        if (selectedFilter === 'geneID') {
          return Array.isArray(result.ensembl_ids)
            ? result.ensembl_ids.join(', ')
            : result.ensembl_ids || 'Not available';
        }
        return result.geneID;
      case 'CHEMICALS':
        return result.chemPrefName;
      default:
        return 'Not available';
    }
  };

  const renderSecondColumn = result => {
    if (searchType === 'DISEASES' || searchType === 'GENES') {
      if (searchType === 'DISEASES' && selectedFilter === 'diseaseUMLSCUI') {
        return <td></td>;
      }
      if (searchType === 'GENES' && selectedFilter === 'symbol') {
        return <td>{result.ensembl_ids || 'Not available'}</td>;
      }
      return (
        <td>
          {searchType === 'DISEASES'
            ? result.diseaseUMLSCUI || 'Not available'
            : result.symbol || 'Not available'}
        </td>
      );
    }
    return null;
  };

  return (
    <div className="suggestions-table">
      <table className="table panther-table" style={{ float: 'middle', margin: '10px 20px 60px 10px' }}>
        <thead>
          <tr>{renderColumnHeader()}</tr>
        </thead>
        <tbody>
          {suggestionsData.map((item, index) => {
            if (item.validationError) {
              if (!uniqueValidationErrors.has(item.query)) {
                uniqueValidationErrors.add(item.query);
                return renderValidationErrorRow(item, index);
              }
              return null;
            }

            if (item.found && Array.isArray(item.results)) {
              return item.results
                .filter(result => {
                  const resultString = JSON.stringify(result);
                  if (uniqueResults.has(resultString)) {
                    return false;
                  }
                  uniqueResults.add(resultString);
                  return true;
                })
                .map((result, i) => renderResultRow(result, index, i, item.query));
            }

            if (!uniqueNotFound.has(item.query) && item.found === false) {
              uniqueNotFound.add(item.query);
              return renderNotFoundRow(item, index);
            }

            return null;
          })}
        </tbody>
      </table>
    </div>
  );
};

export default SuggestionsTable;
